/**
 * StockKitFormDialog - Monta um kit a partir de um rastreador e um chip livres
 *
 * Só lista itens com situação Ativo e sem kit ativo (BR-EST-016/017 — validado também
 * no servidor). O código KIT-000001 é gerado no banco.
 *
 * RELEASE: 2026-10-08 v1.2.0 - Mostra o Endereço de Estoque (Padrão) e bloqueia sem padrão (BR-EST-026).
 * RELEASE: 2026-10-08 v1.1.0 - Seletor com busca (Popover + Command): 10 primeiros itens,
 *          etiqueta de situação, identificador em destaque e modelo/linha+plano abaixo.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 3 do Estoque).
 */
import { useEffect, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { stockLocationTypeLabel, useDefaultStockLocation } from '@/hooks/useStockRegistries';
import { stockStatusLabel, useStockFreeComponents, useStockItemMutations } from '@/hooks/useStockItems';

type Picked = { id: string; label: string } | null;

/** Campo de busca de componentes livres (rastreador ou chip). */
function ComponentPicker({ kind, label, value, onChange, enabled }: {
  kind: 'tracker' | 'chip'; label: string; value: Picked; onChange: (v: Picked) => void; enabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const { data = [], isLoading } = useStockFreeComponents(kind, debounced, enabled);
  const placeholder = kind === 'tracker' ? 'Buscar rastreador...' : 'Buscar chip...';

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
            <span className={cn('truncate tabular-nums', !value && 'text-muted-foreground')}>{value?.label ?? placeholder}</span>
            <span className="flex shrink-0 items-center gap-1 text-muted-foreground"><Search className="h-4 w-4" /><ChevronDown className="h-4 w-4" /></span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          {/* Filtro feito no servidor; desliga o filtro local do Command */}
          <Command shouldFilter={false}>
            <CommandInput value={search} onValueChange={(v) => setSearch(v.slice(0, 30))}
              placeholder={kind === 'tracker' ? 'IMEI ou número de série...' : 'ICCID ou linha...'} />
            <CommandList>
              {isLoading ? <p className="p-3 text-xs text-muted-foreground">Carregando...</p> : (
                <CommandEmpty>Nenhum item ativo e livre encontrado.</CommandEmpty>
              )}
              <CommandGroup>
                {data.map((c) => (
                  <CommandItem key={c.id} value={c.id} onSelect={() => { onChange({ id: c.id, label: c.label }); setOpen(false); }}
                    className="flex items-center gap-3 py-2">
                    <Badge variant="secondary" className="shrink-0">{stockStatusLabel(kind, c.status)}</Badge>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium tabular-nums">{c.label}</p>
                      {c.extra && <p className="truncate text-xs text-muted-foreground">{c.extra}</p>}
                    </div>
                    {value?.id === c.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function StockKitFormDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [tracker, setTracker] = useState<Picked>(null);
  const [chip, setChip] = useState<Picked>(null);
  const [notes, setNotes] = useState('');
  const { formKit } = useStockItemMutations();
  // Destino automático do kit montado
  const { data: defaultLoc, isLoading: loadingLoc } = useDefaultStockLocation(open);

  useEffect(() => {
    if (open) {
      setTracker(null);
      setChip(null);
      setNotes('');
    }
  }, [open]);

  const save = () => {
    if (!tracker || !chip) return;
    formKit.mutate({ trackerId: tracker.id, chipId: chip.id, notes }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Montar kit</DialogTitle>
          <DialogDescription>Selecione um rastreador e um chip. O kit será montado e os itens serão movidos para o endereço de estoque padrão. Só aparecem itens Ativos e fora de kit ativo.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <ComponentPicker kind="tracker" label="Rastreador *" value={tracker} onChange={setTracker} enabled={open} />
          <ComponentPicker kind="chip" label="Chip *" value={chip} onChange={setChip} enabled={open} />
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Endereço de Estoque (Padrão)</Label>
            {defaultLoc ? (
              <div className="rounded-md border bg-muted/40 p-3">
                <p className="font-medium">{defaultLoc.name}</p>
                <p className="text-sm text-muted-foreground">
                  {[stockLocationTypeLabel(defaultLoc.location_type), [defaultLoc.city, defaultLoc.state].filter(Boolean).join('/')].filter(Boolean).join(' - ')}
                </p>
              </div>
            ) : (
              <p className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">
                {loadingLoc ? 'Carregando endereço padrão...' : 'Nenhum endereço de estoque padrão. Marque um em Endereços de Estoque para montar kits.'}
              </p>
            )}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Observações</Label>
            <Textarea value={notes} maxLength={1000} rows={2} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">O código do kit é criado automaticamente. A situação inicial do kit é a mesma do rastreador. Se o item vier de um kit não ativo, esse kit antigo é desmontado automaticamente.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={!tracker || !chip || !defaultLoc || formKit.isPending}>Montar kit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
